import {
  INetsuite,
  NetsuiteCustomerLookupEnum,
  NetsuiteCustomerLookupEnumDisplay,
  NetsuiteProductExtIdEnumDisplay,
  SettingCardViewAction,
} from '@monetize/types/app';
import { FC, ReactNode, useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { useUpdateAccountingConnectionStatus } from '../../../../../api/accountingService';
import { SettingCardEdit } from '../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../components/Settings/SettingCardView';
import { useFlags } from '../../../../../services/launchDarkly';
import { NetSuiteFieldMapping } from './NetSuiteFieldMapping';
import { NetsuiteForm } from './NetsuiteForm';
import { NETSUITE_FORM_FIELDS } from './accounting.constants';

interface NetSuiteConnectionProps {
  connection: INetsuite;
  description?: ReactNode;
}

export const NetSuiteConnection: FC<NetSuiteConnectionProps> = ({
  connection,
  description,
}) => {
  const { allowNetsuiteFieldMapping } = useFlags();
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');

  const { mutate: updateConnection, isPending: isProcessing } =
    useUpdateAccountingConnectionStatus({
      meta: { showErrorToast: true },
    });

  const isEnabled = connection.enabled;

  const handleAction = (action: SettingCardViewAction) => {
    if (isProcessing) {
      return;
    }
    switch (action) {
      case 'DOCS-LINK':
        window.open(
          'https://docs.monetizenow.io/docs/authentication-setup',
          '_blank',
        );
        break;
      case 'ENABLE':
        updateConnection({ type: 'netsuite', action: 'enable' });
        break;
      case 'DISABLE':
        updateConnection({ type: 'netsuite', action: 'disable' });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {mode === 'VIEW' && (
        <SettingCardView
          status={isEnabled ? 'ACTIVE' : 'INACTIVE'}
          isLoading={isProcessing}
          isEnabled={isEnabled}
          actions={[
            // { action: 'RE_AUTHENTICATE', label: 'Re-Authenticate' },
            // { action: 'VERIFY', label: 'Verify Connection' },
            { action: 'DOCS-LINK', label: 'NetSuite', icon: FiExternalLink },
            {
              action: isEnabled ? 'DISABLE' : 'ENABLE',
              label: isEnabled ? 'Disable' : 'Enable',
              isDanger: isEnabled,
              ...(!isEnabled
                ? {}
                : {
                    confirmProps: {
                      title:
                        'Are you sure you want to disable this connection?',
                      description:
                        'This will temporarily deactivate the NetSuite integration. You can re-enable it at any time.',
                    },
                  }),
            },

            // {
            //   action: 'DELETE',
            //   label: 'Delete',
            //   isDanger: true,
            //   confirmProps: {
            //     title: `Are you sure you want to delete this connection?`,
            //     description:
            //       'Once removed, this configuration will not longer be available.',
            //   },
            // },
          ]}
          type="NetSuite"
          tags={[
            {
              label: NETSUITE_FORM_FIELDS.accountId.label,
              value: connection.accountId,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.companyId.label,
              value: connection.companyId,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.consumerKey.label,
              value: connection.consumerKey,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.consumerSecret.label,
              value: connection.consumerSecret,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.tokenId.label,
              value: connection.tokenId,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.tokenSecret.label,
              value: connection.tokenSecret,
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.customerLookup.label,
              tooltip: NETSUITE_FORM_FIELDS.customerLookup.tooltip,
              value:
                NetsuiteCustomerLookupEnumDisplay[
                  connection.customerLookup || NetsuiteCustomerLookupEnum.NONE
                ],
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.netsuiteProductExternalId.label,
              tooltip: NETSUITE_FORM_FIELDS.netsuiteProductExternalId.tooltip,
              value:
                NetsuiteProductExtIdEnumDisplay[
                  connection.netsuiteProductExternalId
                ],
              colSpan: 1,
            },
            {
              label: NETSUITE_FORM_FIELDS.taxItemId.label,
              tooltip: NETSUITE_FORM_FIELDS.taxItemId.tooltip,
              value: connection.taxItemId || 'None',
              colSpan: 1,
            },
          ]}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <>
          {description}
          <SettingCardEdit type="NetSuite">
            <NetsuiteForm
              connection={connection}
              onClose={() => setMode('VIEW')}
            />
          </SettingCardEdit>
        </>
      )}

      {allowNetsuiteFieldMapping && <NetSuiteFieldMapping />}
    </>
  );
};
